import { tagKompakt } from '@netcon-work/shared'
import type { LeitstandHeute } from '../../lib/queries'

export type Punkt = LeitstandHeute['lage']['punkte'][number]

/** Was der Knopf an der Zeile tut. */
export type PunktAktion = 'zuweisen' | 'ansehen' | 'oeffnen' | 'eingang'

export interface PunktText {
  /** Die erste, farbige Zeile: warum steht das hier? */
  warum: string
  /** Die fette Zeile: um was oder wen geht es? */
  was: string
  /** Kleingedrucktes darunter, wenn es etwas gibt. */
  zusatz: string | null
  aktion: PunktAktion
  knopf: string
}

const aufgaben = (n: number) => `${n} ${n === 1 ? 'Aufgabe' : 'Aufgaben'}`

/**
 * **Aus einem Punkt wird ein Satz** ([E-358](../../../../../docs/ENTSCHEIDUNGEN.md#e-358)).
 *
 * Der Server sagt, *was* anliegt; hier steht, wie es sich liest. Jede Zeile beantwortet drei
 * Fragen in fester Reihenfolge – **warum** sie dasteht, **worum** es geht, und **was man tun
 * kann** –, und jede Art bietet genau eine Handlung an. Eine Liste, an der mal zwei, mal drei
 * Knöpfe hängen, muss man zeilenweise lesen; das Daily soll sie überfliegen können.
 */
export function punktText(p: Punkt): PunktText {
  const n = p.ids.length
  const titel = p.aufgabe?.titel ?? ''
  switch (p.art) {
    case 'sofort':
      return {
        warum: `Sofort-Pool · seit ${
          p.stunden !== null && p.stunden < 48 ? `${p.stunden} Std.` : `${p.tage} Tagen`
        } nicht gezogen`,
        was: titel,
        zusatz: null,
        aktion: 'zuweisen',
        knopf: 'zuweisen',
      }
    case 'ueberfaellig_niemand':
      return {
        warum: 'überfällig · niemand dran',
        was: titel,
        zusatz: null,
        aktion: 'zuweisen',
        knopf: 'zuweisen',
      }
    case 'ueberfaellig_abwesend':
      return {
        warum: `${n} überfällig · Bearbeiter abwesend`,
        was: p.personName ?? 'unbekannt',
        zusatz: n === 1 ? titel : null,
        aktion: 'ansehen',
        knopf: 'ansehen',
      }
    case 'ueberfaellig':
      return {
        warum: `${n} überfällig`,
        was: p.personName ?? 'unbekannt',
        zusatz: n === 1 ? titel : null,
        aktion: 'ansehen',
        knopf: 'ansehen',
      }
    case 'bald_abwesend':
      return {
        warum: [
          'bald abwesend',
          p.tage > 0 ? `${p.tage} fällig in ≤ 5 Werktagen` : null,
          p.inArbeit > 0 ? `${p.inArbeit} in Arbeit` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        was: p.personName ?? 'unbekannt',
        zusatz:
          p.naechsteVon && p.naechsteBis
            ? `weg ${tagKompakt(p.naechsteVon)}–${tagKompakt(p.naechsteBis)}`
            : null,
        aktion: 'ansehen',
        knopf: 'übergeben?',
      }
    case 'faellig_niemand':
      return n === 1
        ? {
            warum: 'fällig in ≤ 5 Werktagen · niemand dran',
            was: titel,
            zusatz: null,
            aktion: 'zuweisen',
            knopf: 'zuweisen',
          }
        : {
            warum: `${n} fällig in ≤ 5 Werktagen · niemand dran`,
            was: 'Aufgaben ohne Bearbeiter',
            zusatz: null,
            aktion: 'ansehen',
            knopf: 'verteilen',
          }
    case 'abnahme':
      return {
        warum: [
          `Abnahme wartet ${p.tage} Tage`,
          p.personId === null ? 'kein Abnehmer eingetragen' : null,
          p.abwesendBis ? 'Abnehmer abwesend' : null,
        ]
          .filter(Boolean)
          .join(' · '),
        was: titel,
        zusatz: p.personName ? `Abnehmer: ${p.personName}` : null,
        aktion: 'oeffnen',
        knopf: 'öffnen',
      }
    case 'eingang':
      return {
        warum: `Eingang · ${aufgaben(n)} seit über 7 Tagen ungesichtet`,
        was: 'Eingang sichten',
        zusatz: titel ? `älteste: „${titel}" · ${p.tage} Tage` : null,
        aktion: 'eingang',
        knopf: 'zum Eingang',
      }
    case 'stumm':
      return {
        warum: `in Arbeit, seit ${p.tage} Tagen ohne Bewegung`,
        was: titel,
        zusatz: p.personName,
        aktion: 'oeffnen',
        knopf: 'nachfragen',
      }
  }
}
